import {ExchangeRateSelection, useAppStore} from "@/store/appStore.ts";
import {ExchangeRate} from "@/types";

const MONTH_ABBR = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

export function getMonthAbbr(month: string | number): string {
    const m = Number(month);
    if (!Number.isInteger(m) || m < 1 || m > 12) {
        return "--"
    }
    return MONTH_ABBR[m - 1];
}

export function formatBatch(batch: string | number): string {
    const s = String(batch);
    return s.length === 6 ? `${s.slice(0, 4)}.${s.slice(4, 6)}` : s || '--';
}

export function formatDateTime(value: string | null | undefined): string {
    if (!value) return '--';
    const text = String(value).replace('T', ' ');
    return text.length > 16 ? text.slice(0, 16) : text;
}

export function formatNumber(value: number | null | undefined): string {
    if (value == null) return '--';
    return value.toLocaleString();
}


export function normalizeMonthValue(value: string): string {
    const text = String(value || '').trim();
    return /^\d{6}$/.test(text) ? text : '';
}

export function normalizeMonthSelection(value: string | string[]): string[] {
    const items = Array.isArray(value) ? value : (value ? [value] : []);
    return [...new Set(items.map(normalizeMonthValue).filter(Boolean))].sort();
}


export function resolveLevel(level: string, score: number): string {
    const numericScore = Number(score);
    if (Number.isFinite(numericScore)) {
        if (numericScore >= 0.7) return 'High';
        if (numericScore >= 0.3) return 'Medium';
        return 'Low';
    }
    if (['High', 'Medium', 'Low', 'Normal'].includes(level)) return level;
    return 'Normal';
}

export function levelTheme(level: string) {
    if (level === 'High') return {fg: '#dc2626', bg: '#fef2f2', solid: '#dc2626'};
    if (level === 'Medium') return {fg: '#ea580c', bg: '#fff7ed', solid: '#f97316'};
    if (level === 'Low') return {fg: '#16a34a', bg: '#f0fdf4', solid: '#16a34a'};
    return {fg: '#475569', bg: '#f8fafc', solid: '#64748b'};
}


/**
 * 将数字转换为百分比字符串
 * @param num 需要转换的数字（例如 0.1234）
 * @param decimals 保留的小数位数，默认为 2
 * @returns 格式化后的百分比字符串（例如 "12.34%"）
 */
export function formatPercentage(num: number | null | undefined, decimals: number = 1): string {
    // 1. 处理非数字或无效值
    if (num === null || num === undefined || isNaN(num) || num == 0) return '--';

    // 2. 乘以 100 并保留指定小数位
    // 使用 toFixed 处理浮点数精度问题，同时完成格式化
    const percentage = (num * 100).toFixed(decimals);

    return `${percentage}%`;
    //这是不四舍五入版本
    // if (num === null || num === undefined || isNaN(num)) return '0%';
    //
    // const percentage = num * 100;
    // // 使用 Math.floor 进行截断，而不是 toFixed 的四舍五入
    // // 10 的 decimals 次方，用于保留小数位
    // const factor = Math.pow(10, decimals);
    // const truncated = Math.floor(percentage * factor) / factor;
    //
    // // 转为字符串并去除末尾多余的 0
    // const cleanNum = parseFloat(truncated.toFixed(decimals)).toString();
    //
    // return `${cleanNum}%`;
}


export type CompactUnit = 'K' | 'M' | 'B' | 'T' | 'auto';

/**
 * 将数字格式化为 K, M, B 等缩写形式
 * @param num 需要格式化的数字
 * @param decimals 保留的小数位数，默认为 1
 * @param targetUnit 指定目标单位 ('K', 'M', 'B', 'T')，默认为 'auto' (自动判断)
 * @param selectExchange
 * @param show_prefx 是否显示前缀，默认为 true
 */
export const formatCompactNumber=(
    num: number | null | undefined,
    decimals: number = 1,
    targetUnit: CompactUnit = 'auto',
    selectExchange:ExchangeRateSelection|null = null,
    show_prefx:boolean = true
): string => {
    // 处理非数字或无效值
    if (num === null || num === undefined || isNaN(num) || num == 0) return '--';
    if (!selectExchange){
        try {
            selectExchange = useAppStore().selectExchange
        }catch (e) {
            console.log(e)
        }
    }
    if (selectExchange) {
        num = num * selectExchange.exchangeRate
    }

    // 处理负数
    const isNegative = num < 0;
    const absNum = Math.abs(num);

    // 定义单位阶梯
    const units = [
        {value: 1e12, suffix: 'T'}, // Trillion
        {value: 1e9, suffix: 'B'},  // Billion
        {value: 1e6, suffix: 'M'},  // Million
        {value: 1e3, suffix: 'K'},  // Thousand
    ];

    let chosenUnit: { value: number; suffix: string } | undefined;

    // 1. 如果指定了目标单位，强制使用
    if (targetUnit !== 'auto') {
        chosenUnit = units.find(u => u.suffix === targetUnit.toUpperCase());
    }

    // 2. 如果未指定，或者指定的单位不在列表中，自动判断
    if (!chosenUnit) {
        // 小于 1000 直接返回
        if (absNum < 1000) {
            return `${isNegative ? '-' : ''}${absNum.toFixed(decimals)}`;
        }
        chosenUnit = units.find(u => absNum >= u.value);
    }

    // 如果依然找不到合适的单位（例如数字太小但强制要求 K），直接返回原数字
    if (!chosenUnit) {
        return `${isNegative ? '-' : ''}${absNum.toFixed(decimals)}`;
    }

    // 计算并格式化
    const formatted = (absNum / chosenUnit.value).toFixed(decimals);

    // 去除末尾多余的 0 (例如 1.0K -> 1K, 1.50K -> 1.5K)
    // const cleanNum = parseFloat(formatted).toString();
    return `${show_prefx ? selectExchange && selectExchange.show_char : ''}${isNegative ? '-' : ''}${formatted}${chosenUnit.suffix}`;
}